/**
 * Per-entity cluster substrate queries for Atlas Day 2 Phase 3.
 *
 * Single async function fetches all 5 entity-type substrate counts +
 * derived metrics for the Per-entity cluster sub-region cards. Per
 * Phase 0 #1-#5 verified shapes:
 *
 *   distributors   — total / active count; primary distributor (BLVS today)
 *   suppliers      — total / active count; lead-time range (D3 N=0 today)
 *   brands         — total / active count; enforces_map count
 *   manufacturers  — total / active count; manufacturer_distributors pairing count
 *   categories     — total / active count (D3 N=0 today)
 *
 * Uses arbi-dev's own supabase client (no cross-app cache sharing per
 * Atlas Day 1 Phase 0 #12 disposition). No caching at Day 2; direct fetch
 * on cluster mount. Day 3+ may add caching if needed.
 *
 * Conditional render data per D3 lock (c) — N=0 conditional copy applies
 * at card render via PerSupplierCard + PerCategoryCard with eventual-
 * existence framing ("ISG has not yet onboarded any suppliers" /
 * "ISG has not yet configured category taxonomy").
 */

import { supabase } from '../supabase';

// ── Types ────────────────────────────────────────────────────────────────────

export interface DistributorSubstrate {
  total: number;
  active: number;
}

export interface SupplierSubstrate {
  total: number;
  active: number;
  /** Range of active suppliers' lead_time_days; null when no active suppliers */
  leadTimeRange: { min: number; max: number } | null;
}

export interface BrandSubstrate {
  total: number;
  active: number;
  enforcesMap: number;
}

export interface ManufacturerSubstrate {
  total: number;
  active: number;
  pairingCount: number;
}

export interface CategorySubstrate {
  total: number;
  active: number;
}

export interface PerEntitySubstrate {
  distributors: DistributorSubstrate;
  suppliers: SupplierSubstrate;
  brands: BrandSubstrate;
  manufacturers: ManufacturerSubstrate;
  categories: CategorySubstrate;
}

// ── Fetcher ──────────────────────────────────────────────────────────────────

export async function fetchPerEntitySubstrate(): Promise<PerEntitySubstrate> {
  const [
    distAll,
    distActive,
    supAll,
    supActiveRows,
    brAll,
    brActive,
    brMap,
    mfAll,
    mfActive,
    mdAll,
    catAll,
    catActive,
  ] = await Promise.all([
    supabase.from('distributors').select('id', { count: 'exact', head: true }),
    supabase
      .from('distributors')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true),
    // K1.5: suppliers table dropped — N=0 guard (no query). The full
    // Per-Supplier→Per-Manufacturer Atlas rename is deferred to a dev-coherence slice.
    Promise.resolve({ count: 0 }),
    Promise.resolve({ data: [] as Array<{ lead_time_days: number | null }> }),
    supabase.from('brands').select('id', { count: 'exact', head: true }),
    supabase.from('brands').select('id', { count: 'exact', head: true }).eq('is_active', true),
    supabase.from('brands').select('id', { count: 'exact', head: true }).eq('enforces_map', true),
    supabase.from('manufacturers').select('id', { count: 'exact', head: true }),
    supabase
      .from('manufacturers')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true),
    supabase.from('manufacturer_distributors').select('id', { count: 'exact', head: true }),
    supabase.from('categories').select('id', { count: 'exact', head: true }),
    supabase.from('categories').select('id', { count: 'exact', head: true }).eq('is_active', true),
  ]);

  const supLeadTimes = ((supActiveRows.data ?? []) as Array<{ lead_time_days: number | null }>)
    .map((r) => r.lead_time_days)
    .filter((v): v is number => v != null);
  const leadTimeRange =
    supLeadTimes.length > 0
      ? { min: Math.min(...supLeadTimes), max: Math.max(...supLeadTimes) }
      : null;

  return {
    distributors: {
      total: distAll.count ?? 0,
      active: distActive.count ?? 0,
    },
    suppliers: {
      total: supAll.count ?? 0,
      active: supActiveRows.data?.length ?? 0,
      leadTimeRange,
    },
    brands: {
      total: brAll.count ?? 0,
      active: brActive.count ?? 0,
      enforcesMap: brMap.count ?? 0,
    },
    manufacturers: {
      total: mfAll.count ?? 0,
      active: mfActive.count ?? 0,
      pairingCount: mdAll.count ?? 0,
    },
    categories: {
      total: catAll.count ?? 0,
      active: catActive.count ?? 0,
    },
  };
}
